import { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { getFirebaseDb } from '../services/firebase';
import {
  createTodoProject,
  normalizeTodoProject,
  renameTodoProject,
  setTodoProjectArchived,
  setTodoProjectColor,
  setTodoProjectPinned,
  deleteTodoProject,
  countCardsReferencingProject,
  clearProjectFromAllCards,
} from '../services/todoProjects';
import type { TodoProject, TodoProjectColorKey } from '../types';

interface UseTodoProjectsReturn {
  projects: TodoProject[];
  loading: boolean;
  byId: Map<string, TodoProject>;
  createProject: (name: string, colorKey?: TodoProjectColorKey) => Promise<string | null>;
  renameProject: (projectId: string, name: string) => Promise<void>;
  setColor: (projectId: string, colorKey: TodoProjectColorKey | null) => Promise<void>;
  setArchived: (projectId: string, archived: boolean) => Promise<void>;
  setPinned: (projectId: string, pinned: boolean) => Promise<void>;
  countReferencingCards: (projectId: string) => Promise<{ tasks: number; focuses: number }>;
  moveAllToInbox: (projectId: string) => Promise<{ taskIds: string[]; focusIds: string[] }>;
  deleteProject: (projectId: string) => Promise<void>;
}

export function useTodoProjects(userId: string | null): UseTodoProjectsReturn {
  const [projects, setProjects] = useState<TodoProject[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setProjects([]);
      setLoading(false);
      return;
    }

    const db = getFirebaseDb();
    const q = query(
      collection(db, 'users', userId, 'todoProjects'),
      orderBy('updatedAt', 'desc')
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        setProjects(snap.docs.map((d) => normalizeTodoProject(d.id, d.data())));
        setLoading(false);
      },
      () => setLoading(false)
    );

    return unsub;
  }, [userId]);

  const byId = useMemo(() => {
    const map = new Map<string, TodoProject>();
    for (const p of projects) map.set(p.id, p);
    return map;
  }, [projects]);

  const createProject = useCallback(
    async (name: string, colorKey?: TodoProjectColorKey) => {
      if (!userId) return null;
      return createTodoProject(userId, { name, colorKey });
    },
    [userId]
  );

  const renameProject = useCallback(
    async (projectId: string, name: string) => {
      if (!userId) return;
      await renameTodoProject(userId, projectId, name);
    },
    [userId]
  );

  const setColor = useCallback(
    async (projectId: string, colorKey: TodoProjectColorKey | null) => {
      if (!userId) return;
      await setTodoProjectColor(userId, projectId, colorKey);
    },
    [userId]
  );

  const setArchived = useCallback(
    async (projectId: string, archived: boolean) => {
      if (!userId) return;
      await setTodoProjectArchived(userId, projectId, archived);
    },
    [userId]
  );

  const setPinned = useCallback(
    async (projectId: string, pinned: boolean) => {
      if (!userId) return;
      await setTodoProjectPinned(userId, projectId, pinned);
    },
    [userId]
  );

  const countReferencingCards = useCallback(
    async (projectId: string) => {
      if (!userId) return { tasks: 0, focuses: 0 };
      return countCardsReferencingProject(userId, projectId);
    },
    [userId]
  );

  const moveAllToInbox = useCallback(
    async (projectId: string) => {
      if (!userId) return { taskIds: [], focusIds: [] };
      return clearProjectFromAllCards(userId, projectId);
    },
    [userId]
  );

  const deleteProject = useCallback(
    async (projectId: string) => {
      if (!userId) return;
      await deleteTodoProject(userId, projectId);
    },
    [userId]
  );

  return {
    projects,
    loading,
    byId,
    createProject,
    renameProject,
    setColor,
    setArchived,
    setPinned,
    countReferencingCards,
    moveAllToInbox,
    deleteProject,
  };
}
